import Log from "@/utils/Log";
import Vue from "vue";
import SessionManager from "@/utils/SessionManager";

export default class Activity {

    constructor(){
        this.methods = {};
        this.permission = null;

        Object.getOwnPropertyNames(Activity.prototype).forEach(method => {
            this.methods[method] = Activity.prototype[method];
        });

        this.unmountTriggers = [];
    }

    /*  ******************** Behavior configuration ********************  */

    /**
     * Override to disable activity close. Usefull for first activity, to ensure it's always open.
     * When false is returned the activity will never be closed, even when close(force=true)
     * @returns {boolean}
     */
    isClosable(){
        return true;
    }


    /**
     * Override to control the activity close process. When user will try to close the activity, manager will call
     * this method and close it once the Promise is fulfilled. Overloading this method you can display a confirmation 
     * dialog or save unsaved data before the object is destroyed. To cancel close oparation just resolve the promise with false
     * .
     * @return {Promise<boolean>} 
     */
    canClose(){
        return Promise.resolve(true);
    }
    
    /**
     * Override to provide custom title which will be displayed on the breadcrums
     * @return {string} 
     */
    getTitle(trace=false){
        if(trace){
            return true;
        }
        return "" 
    }    

    refreshTitle(){
        this._refreshTrigger();
    }

    getTitleIcon(){
        return null;
    }

    blinkOnBar(){
        this._blinkOnBar();
    }
    /**
     * Override to limit access to activity based on customized criteria
     * @return {Permission>}
     */
    checkRequiredPermissions(){
        if(!this.hasOwnProperty("permission")){
            return true;
        }

        if(_.isFunction(this.permission)){
            this.permission = this.permission();
        }
        let perm = this.permission;
        if(!perm){
            return true;
        }
        return SessionManager.hasPermission(perm);
    }


    /**
     * Override to prevent navigator to show title on the path
     * @return {string} Activity.DESIGNER | Activity.USER | Activity.SETTINGS 
     */
    getActivityType(){
        if(!this._activityType) {
            return Activity.DEFAULT;
        }
        return this._activityType;
    }

    setActivityType(type){
        this._activityType = type;
        this._getActivityWorkspace()._refreshTrigger(this);
    }

    /**
     * Override to prevent navigator to show title on the path
     * @return {string} Activity.ON_PATH | Activity.NONE | Activity.AS_TITLE 
     */
    showOnNavigator(){
        return Activity.ON_PATH; 
    }  
    
    /**
     * what to do when activity is open for the second time
     * @return {string} Activity.ALLOW | Activity.BLOCK | Activity.BUBBLE
     */
    multipleInstances(){
        return Activity.ALLOW; 
    }       


    /*  ******************** Notification ********************  */
    /*
        Lifecycle:

        beforeMount()  --> [mounted to DOM] --> afterMount() -->  
          [[ repeat:  beforeShow() --> afterShow() --> beforeHide() --> afterHide() ]] --> 
        canClose() --> beforeClose() --> beforeUnmount() --> afterUnmount() --> [getResult().resolved] --> onDestroy()

    */

    /** 
    * Override to bind action before your component is attached to the DOM for the first time
    */
    beforeMount() {
    }

    /** 
    * Override to bind action just after (in the same loop tick) your component is attached to the DOM for the first time
    */
    afterMount(){
    }

    willReopen(){

    }

    /** 
    * Override to bind action before your component is going to be shown - it will happen when it become to the top of the stack 
    * (just after creation or when all activities above where closed) 
    */
    beforeShow(){
    }

    /** 
    * Override to bind action after (in next loop tick) your component is going to be shown - it will happen when it become to the top of the stack 
    * (just after creation or when all activities above where closed) 
    */
    afterShow(){
    }


    /** 
    * Override to bind action before your component is hidden, when the other activity is going to be shown on the top o it
    */
    beforeHide(){
    }

    /** 
    * Override to bind action after (in next loop tick) your component is hidden, when the other activity is going to be shown on the top o it
    */
    afterHide(){
    }


    /**
     * Override to bind action after the close operation is confirmed (canClose returns), but before getResult promise is resolved
     * Could be usefull to inject final activity result.
     */
    beforeClose(){
    }    

    /** 
    * Override to bind action before your component is removed from the DOM during the close operation
    */
    beforeUnmount() {
    }

    /** 
    * Override to bind action after (in next loop tick) your component is removed from the DOM during the close operation
    */
    afterUnmount(){
    }           

    /** 
    * This is the last method triggered by Manager in the component lifecycle (in a separate loop tick), after activity is hidden and unmounted. 
    * Use it to clean up if needed.
    */
    onDestroy(){
    }

    sizeAdjusted(){

    }

    onKeyDown(event){

    }
    /*  ******************** Interactions ********************  */

    /**
     * Call to set the activity result. The same result is returned by the promise in the getResult() once the Activity is closed
     * 
     * @param {*} result
     */
    setResult(result){
        this._getActivityWorkspace().setActivityResult(this, result);
    }

    onShowCallback(callback){
        if(!this.__onShowCallbacks){
            this.__onShowCallbacks = [];
        }
        this.__onShowCallbacks.push(callback);
    }

    _runOnShowCallbacks(){
        if(this.__onShowCallbacks){
            this.__onShowCallbacks.forEach(callback => {
                try{
                    callback();
                } catch (e){
                    console.log(e);
                }
            })
        }
    }
    onHideCallback(callback){
        if(!this.__onHideCallbacks){
            this.__onHideCallbacks = [];
        }
        this.__onHideCallbacks.push(callback);
    }

    _runOnHideCallbacks(){
        if(this.__onHideCallbacks){
            this.__onHideCallbacks.forEach(callback => {
                try{
                    callback();
                } catch (e){
                    console.log(e);
                }
            })
        }
    }


    /**
     * Once the Activity is closed the activity result is returned.
     * It coud be used to collect data from created activity like:
     *   let result = await ActivityManager.showActivity(MyActivity).getResult();
     * 
     * @return {Promise<*>} 
     */
    getResult(){
        return this._getActivityWorkspace().getActivityResult(this);
    }


    /**
     * Call to close the activity. If it's not on the top of the stack, this will trigger recursive close for all activities covering it.
     * 
     * @param {boolean=} force - set to true to ski th 'canClose' call
     * @return {Promise<boolean>} 
     */
    close(force){
        return this._getActivityWorkspace().closeActivity(this, force);
    }

    /**
     * @return {boolean} returns true when activity is attached to the workspace
     */
    isOnStack(){
        return this._getActivityWorkspace().isActivityOnStack(this);
    }     

    /**
     * @return {boolean} returns true when activity is the top activity
     */
    isOnTop(){
        return this._getActivityWorkspace().isActivityOnTop(this);
    }


    /**
     * Register callback that will be run when activity is destroyed
     * @param callback
     */
    addUnmountTrigger(callback){
        this.unmountTriggers.push(callback);
    }

    /*  ******************** Internals ********************  */

    /**
     * @return {ActivityWorkspace}
     */
    _getActivityWorkspace(){
        return this._workspace;
    }      

    /**
     */
    _refreshTrigger(){
        this._getActivityWorkspace()._refreshTrigger(this);
    }

    _blinkOnBar(){
        this._getActivityWorkspace()._blinkOnBar(this);
    }

    /**
     * @return {ActivityManager}
     */
    _getActivityManager(){
        return this._activityManager;
    }  

    /**
     * 
     * @param {ActivityManager} activityManager
     */
    _bindActivityManager(activityManager){
        this._activityManager = activityManager;
        this._workspace = activityManager.workspace;
    }

    /**
     *
     * @param {Function} callback
     */
    afterStart(callback){
        if(this.__started){
            callback();
            return;
        }
        if(this.__autostartList === undefined){
            this.__autostartList = [];
        }
        this.__autostartList.push(callback);
    }

    _autostart(){
        if(this.__created === undefined || this.__mounted === undefined){
            return;
        }
        this.__started = true;
        let all = [];
        if(this.__autostartList){
            this.__autostartList.forEach(callback => all.push(callback()));
        }
        return Promise.all(all);
    }

    isActivity(){
        return true;
    }

}

Activity.findWorkspaceRootElement = function(domElement){
    let node = domElement.parentElement;
    while(node && node != document.body){
        if(node?.hasAttribute("data-workspace-root")){
            return node;
        }
        node = node?.parentNode;
    }
    return null;
}

/**
 * @param domElement
 * @return {HTMLElement|null}
 */
Activity.findActivityRootDiv = function(domElement){
    let node = domElement;
    while(node && node != document.body){
        if(node.hasAttribute && (typeof node.hasAttribute === 'function') && node?.hasAttribute("data-activity-root")){
            return node;
        }
        node = node?.parentNode;
    }
    return null;
}

/**
 * @param domElement
 * @return {string|null}
 */
Activity.findActivityClass = function(domElement){
    return Activity.findActivityRootDiv(domElement)?.getAttribute("data-activity-class")
}

/**
 * @param domElement
 * @return {string|null}
 */
Activity.findActivityId = function(domElement){
    if(domElement instanceof Vue){
        return Activity.findActivityId(domElement.$el);
    }
    return Activity.findActivityRootDiv(domElement)?.getAttribute("data-activity-instance-id");
}

/** @type {Activity} */
Activity.instance = new Activity();
Activity.methods = Activity.instance.methods;
Activity.methods['getTitle'].default = true;

/* multipleInstances results */
Activity.BUBBLE = "BUBBLE";
Activity.ALLOW = "ALLOW";
Activity.BLOCK = "BLOCK";

/* showOnNavigator results */
Activity.ON_PATH = "ON_PATH";
Activity.HIDDEN = "HIDDEN";
Activity.AS_TITLE = "AS_TITLE";

/* getActivityType */
Activity.DEFAULT = "";
Activity.SELECT = "select";
Activity.SELECT_REPLACEMENT = "select-replacement";

Activity.registerName = Activity.instance.registerName = function(activityClass) {
    const ActivityManager = require("./ActivityManager").default;
    ActivityManager.registerName(activityClass);
    return activityClass;
}
